import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';

export interface IdentificationDocumentData {
  readonly id: number;
  readonly documentNumber?: string;
  readonly pinCode?: string;
  readonly issuingAuthority?: string;
  readonly dateOfIssue?: string;
  readonly dateOfExpiry?: string;
  readonly visaNumber?: string;
  readonly visaExpirationDate?: string;
  readonly candidateId: number;
  readonly candidate?: Candidate;
}

export interface IdentificationDocument extends Readonly<IdentificationDocumentData> {}
export class IdentificationDocument extends BusinessObjectBase {
  static readonly entityName = 'IdentificationDocument' as const;
  static readonly plural = 'identification-documents';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<IdentificationDocument[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new IdentificationDocument(data));
  }

  static async findOne(filter?: any): Promise<IdentificationDocument | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new IdentificationDocument(data) : null;
  }

  static async findById(
    id: number,
    filter?: any,
  ): Promise<IdentificationDocument> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new IdentificationDocument(data);
  }

  static async create(
    data: Omit<IdentificationDocumentData, 'id'>,
  ): Promise<IdentificationDocument> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new IdentificationDocument(result);
  }

  static async updateById(
    id: number,
    data: Partial<IdentificationDocumentData>,
  ): Promise<IdentificationDocument> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new IdentificationDocument(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
